<script setup>
import { computed } from 'vue'
import { scheduleTypeMeta } from './mediaflowMeta'
import { CREATABLE_SCHEDULE_TYPES } from './adScheduleForm'

const props = defineProps({
	modelValue: { type: String, default: null },
	disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

/**
 * The types an operator can pick, plus the current one when it is a legacy
 * type the picker no longer offers — so editing such a schedule still shows
 * what it is instead of an empty picker.
 */
const options = computed(() => {
	const types = [...CREATABLE_SCHEDULE_TYPES]
	if (props.modelValue && !types.includes(props.modelValue)) types.push(props.modelValue)
	return types.map((value) => ({ value, ...scheduleTypeMeta(value) }))
})
</script>

<template>
	<div class="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Schedule type">
		<button
			v-for="option in options"
			:key="option.value"
			type="button"
			role="radio"
			:aria-checked="modelValue === option.value"
			:disabled="disabled"
			:data-testid="`schedule-type-${option.value}`"
			class="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left ring-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-60"
			:class="modelValue === option.value ? 'bg-surface-4 ring-main' : 'bg-surface-3 ring-transparent hover:ring-surface-6'"
			@click="emit('update:modelValue', option.value)"
		>
			<span class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md" :class="modelValue === option.value ? 'bg-main-transparent text-main-light' : 'bg-surface-2 text-surface-8'">
				<component :is="option.icon" />
			</span>
			<span class="flex min-w-0 flex-col gap-0.5">
				<span class="text-tiny font-medium text-surface-text-2">{{ option.label }}</span>
				<span class="text-xs leading-snug text-surface-8">{{ option.description }}</span>
			</span>
		</button>
	</div>
</template>
